import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { Modal } from '@JohnLewisPartnership/jl-design-system/dist/components/modal/Modal';
import { STRICT_EMAIL } from '@JohnLewisPartnership/jl-design-system/dist/form/fields/email';
import FormField from '@JohnLewisPartnership/jl-design-system/dist/components/form-field/FormField';
import { PrimaryButton, SecondaryButton } from '@JohnLewisPartnership/jl-design-system/dist/elements/button/Button';
import { EmailMeModalProps } from './EmailMeModal.d';
import { ReactComponent as HeartCircleImage } from '../../assets/icons/heart-circle.svg';
import styles from './EmailMeModal.module.scss';
import { Analytics, ACTION, LOCATION, DESCRIPTION } from '../../helpers/analytics/analytics';

export const EmailMeModal: React.FC<EmailMeModalProps> = observer((props: EmailMeModalProps) => {
    const { emailModalStore } = props;
    const { emailMeSubmitting, isEmailMeModalOpen, emailMeEmailError, toggleEmailFieldTouched, showEmailMeError } =
        emailModalStore;

    useEffect(() => {
        if (isEmailMeModalOpen) {
            Analytics.dispatchAdobeAnalytics(
                ACTION.EMWBIS_MODAL_OPEN,
                LOCATION.PDP_WISH_LIST_WIDGET,
                DESCRIPTION.EMWBIS,
            );
        }
    }, [isEmailMeModalOpen]);

    if (!isEmailMeModalOpen) return null;

    return (
        <Modal
            data-testid="emwbis-model"
            isOpen={true}
            title="Email when in stock"
            onClose={(): void => emailModalStore.closeEmailMeModal()}
            footer={false}
        >
            <div id="email-me-modal" className={styles.modalContent} data-testid="email-me-modal">
                <div className={styles.notificationContainer}>
                    <HeartCircleImage aria-hidden="true" className={styles.notificationImage} />
                    <span className={styles.notificationText}>We&apos;ve added this item to your wish list.</span>
                </div>
                <>
                    <p className={styles.infoText}>
                        If you&apos;d like to be notified when it&apos;s available, enter your email address below.
                    </p>

                    <p className={styles.infoText}>
                        If it doesn&apos;t come back into stock, we&apos;ll help you with similar product suggestions.
                    </p>

                    <div className={styles.fullWidth}>
                        <FormField
                            input={{
                                name: 'email-me-email',
                                value: emailModalStore.customerEmail,
                                onFocus: (): void => {
                                    // this is just here to suppress a console error
                                    return;
                                },
                                onChange: (e: React.ChangeEvent<HTMLInputElement>): void => {
                                    emailModalStore.customerEmail = e.target.value;
                                },
                            }}
                            label="Email"
                            element={STRICT_EMAIL.component}
                            required={true}
                            key="email-me-key"
                            id="email-me-id"
                            data-testid="email-me-form"
                            onBlur={toggleEmailFieldTouched}
                            highlightError={showEmailMeError}
                            meta={{ error: emailMeEmailError }}
                            autoFocus={true}
                        />
                    </div>

                    <div className={styles.buttonContainer}>
                        <SecondaryButton
                            inline
                            data-testid="cancel-btn"
                            onClick={(): void => emailModalStore.closeEmailMeModal()}
                        >
                            Cancel
                        </SecondaryButton>
                        <PrimaryButton
                            inline
                            data-testid="continue-btn"
                            submitting={emailMeSubmitting}
                            onClick={(): Promise<void> => emailModalStore.emailMe()}
                        >
                            Notify me
                        </PrimaryButton>
                    </div>
                </>
            </div>
        </Modal>
    );
});
